import PropTypes from 'prop-types';
import { CheckCircle } from 'lucide-react';

const CLICKUP_GRADIENT_ID = 'integration-provider-clickup-gradient';

function ProviderSvg({ size = 20, children, ...props }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

ProviderSvg.propTypes = {
  size: PropTypes.number,
  children: PropTypes.node,
};

export function normalizeIntegrationProviderKey(provider) {
  const key = String(provider || '').toLowerCase().trim();
  if (!key) return null;

  if (key.includes('jira')) return 'jira';
  if (key.includes('trello')) return 'trello';
  if (key.includes('github') && key.includes('issue')) return 'github';
  if (key.includes('github')) return 'github';
  if (key.includes('gitlab') && key.includes('issue')) return 'gitlab';
  if (key.includes('gitlab')) return 'gitlab';
  if (key.includes('azure') && key.includes('board')) return 'azure-boards';
  if (key.includes('azure')) return 'azure-devops';
  if (key.includes('clickup') || key.includes('click up')) return 'clickup';
  if (key.includes('bitbucket') || key.includes('bit bucket')) return 'bitbucket';
  if (key.includes('testrail') || key.includes('test rail')) return 'testrail';
  if (key.includes('xray')) return 'xray';
  if (key.includes('zephyr')) return 'zephyr';

  return key;
}

const PROVIDER_ICON_RENDERERS = {
  jira: (size) => (
    <ProviderSvg size={size}>
      <path
        d="M10.12 21.55a.76.76 0 01-.68.45c-.15 0-.29-.05-.42-.14L1.22 15.44a.74.74 0 010-1.29l9.45-6.02c.24-.15.55-.15.8 0L21.15 14.5a.74.74 0 010 1.29l-8.45 5.97a.76.76 0 01-.68.16c-.09 0-.18-.03-.27-.07L12 19.68l-1.88 1.87z"
        fill="#0052CC"
      />
    </ProviderSvg>
  ),
  trello: (size) => (
    <ProviderSvg size={size}>
      <path
        d="M3 3C3 2.44772 3.44772 2 4 2H20C20.5523 2 21 2.44772 21 3V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V3Z"
        fill="#0079BF"
      />
      <path
        d="M7 6C6.44772 6 6 6.44772 6 7V14C6 14.5523 6.44772 15 7 15H10C10.5523 15 11 14.5523 11 14V7C11 6.44772 10.5523 6 10 6H7Z"
        fill="white"
      />
      <path
        d="M14 6C13.4477 6 13 6.44772 13 7V11C13 11.5523 13.4477 12 14 12H17C17.5523 12 18 11.5523 18 11V7C18 6.44772 17.5523 6 17 6H14Z"
        fill="white"
      />
    </ProviderSvg>
  ),
  github: (size) => (
    <ProviderSvg size={size}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.165 6.84 9.489.5.092.682-.217.682-.481v-1.856c-2.785.604-3.37-1.337-3.37-1.337-.455-1.157-1.11-1.466-1.11-1.466-.908-.622.07-.61.07-.61 1.004.07 1.53 1.031 1.53 1.031.893 1.531 2.341 1.088 2.915.832.092-.647.35-1.088.636-1.338-2.221-.252-4.555-1.11-4.555-4.945 0-1.092.39-1.988 1.03-2.685-.103-.253-.446-1.27.097-2.647 0 0 .84-.268 2.75 1.02a9.56 9.56 0 012.5-.336c.847 0 1.7.114 2.5.336 1.91-1.288 2.75-1.02 2.75-1.02.543 1.377.2 2.394.098 2.647.641.697 1.03 1.593 1.03 2.685 0 3.842-2.336 4.688-4.566 4.938.361.311.68.927.68 1.868v2.775c0 .267.182.577.688.48C19.14 20.163 22 16.42 22 12c0-5.52-4.48-10-10-10z"
        fill="#5C6BC0"
      />
    </ProviderSvg>
  ),
  gitlab: (size) => (
    <ProviderSvg size={size}>
      <path
        d="M12 2L5.429 21h3.286l.714-2h8.286l.714 2H20.57l-5.142-15.857L12 2zm-3.095 3.428L9.381 9.571l1.857-3.143 1.857-2.857h3.238l-4.524 4.857zM5.286 18l2.286-5.286L7.286 18H5.286z"
        fill="#FC6D26"
      />
    </ProviderSvg>
  ),
  'azure-boards': (size) => (
    <ProviderSvg size={size}>
      <path
        d="M14.333 3L5.429 21h3.286l.714-2h8.286l.714 2H20.57l-5.142-15.857L14.333 3zm-3.095 3.428L9.381 9.571l1.857-3.143 1.857-2.857h3.238l-4.524 4.857zM5.286 18l2.286-5.286L7.286 18H5.286z"
        fill="#066FD1"
      />
    </ProviderSvg>
  ),
  'azure-devops': (size) => (
    <ProviderSvg size={size}>
      <path
        d="M12.7498 0.877898L23.1227 9.87498C23.4926 10.179 23.4926 10.8185 23.1227 11.1225L12.7498 20.1196C12.3799 20.4236 11.8801 20.4236 11.5102 20.1196L1.13731 11.1225C0.767361 10.8185 0.767361 10.179 1.13731 9.87498L11.5102 0.877898C11.8801 0.573911 12.3799 0.573911 12.7498 0.877898Z"
        fill="#0089D6"
      />
    </ProviderSvg>
  ),
  clickup: (size) => (
    <ProviderSvg size={size}>
      <defs>
        <linearGradient
          id={CLICKUP_GRADIENT_ID}
          x1="2"
          y1="24"
          x2="22"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#8930FD" />
          <stop offset="1" stopColor="#FF02F0" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${CLICKUP_GRADIENT_ID})`}
        d="m2 18.439 3.69-2.828c1.961 2.56 4.044 3.739 6.363 3.739 2.307 0 4.33-1.166 6.203-3.704L22 18.405C19.298 22.065 15.941 24 12.053 24 8.178 24 4.788 22.078 2 18.439M12.04 6.15l-6.568 5.66-3.036-3.52L12.055 0l9.543 8.296-3.05 3.509z"
      />
    </ProviderSvg>
  ),
  bitbucket: (size) => (
    <ProviderSvg size={size}>
      <path
        d="M4.11 7.72l7.06 11.69c.06.1.15.16.26.16h.12c.11 0 .2-.06.26-.16l7.06-11.69c.05-.08.07-.18.05-.28a.48.48 0 00-.15-.28.46.46 0 00-.28-.11h-14.1c-.1 0-.21.04-.28.11a.48.48 0 00-.15.28.47.47 0 00.05.28zm6.55-.36l-1.5-2.5A.48.48 0 019.56 4H14.4a.48.48 0 01.42.22l1.5 2.5a.47.47 0 01-.41.7H11.07a.47.47 0 01-.41-.7z"
        fill="#0052CC"
      />
    </ProviderSvg>
  ),
  testrail: (size) => (
    <ProviderSvg size={size}>
      <path
        fill="#58f962"
        d="M7.27 23.896L4.5 21.124a.35.35 0 0 1 0-.5l2.772-2.77a.35.35 0 0 1 .5 0l2.772 2.772a.35.35 0 0 1 0 .5l-2.772 2.77a.35.35 0 0 1-.5 0zm4.48-4.48l-2.772-2.772a.35.35 0 0 1 0-.498l2.772-2.772a.35.35 0 0 1 .5 0l2.77 2.772a.35.35 0 0 1 0 .5l-2.77 2.77a.35.35 0 0 1-.499 0zm4.48-4.48l-2.77-2.772a.35.35 0 0 1 0-.498l2.771-2.772a.35.35 0 0 1 .5 0l2.77 2.772a.35.35 0 0 1 0 .498l-2.772 2.772a.35.35 0 0 1-.5 0h.002zm-8.876.084l-2.772-2.77a.35.35 0 0 1 0-.499l2.772-2.773a.35.35 0 0 1 .5 0l2.772 2.772a.35.35 0 0 1 0 .498l-2.772 2.774a.35.35 0 0 1-.5 0zm4.48-4.48L9.062 7.77a.35.35 0 0 1 0-.5l2.772-2.772a.35.35 0 0 1 .5 0l2.77 2.772a.35.35 0 0 1 0 .498l-2.77 2.772a.35.35 0 0 1-.499 0v-.002v.001zM7.44 6.15L4.666 3.37a.35.35 0 0 1 0-.5L7.44.104a.35.35 0 0 1 .5 0l2.772 2.772a.35.35 0 0 1 0 .5L7.938 6.142a.35.35 0 0 1-.5 0l.002.006z"
      />
    </ProviderSvg>
  ),
  xray: (size) => (
    <ProviderSvg size={size}>
      <path d="M15 8L7.5 8L15 1L15 8Z" fill="#4CAF50" />
      <path d="M7.5 8L7.5 14.5L12.5 10.5L15 8L7.5 8Z" fill="#4CAF50" />
      <path d="M7.5 14.5L12.5 10.5L17.5 12L13.5 15L7.5 14.5Z" fill="#66BB6A" />
      <path d="M17.5 12L13.5 15L10 23L17.5 12Z" fill="#98E1AA" />
    </ProviderSvg>
  ),
  zephyr: (size) => (
    <ProviderSvg size={size}>
      <path d="M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v14h14V5H5zm7 2a5 5 0 1 1-5 5h2a3 3 0 1 0 3-3V7z" fill="#0072CE" />
    </ProviderSvg>
  ),
};

export function IntegrationProviderIcon({ provider, size = 20 }) {
  const providerKey = normalizeIntegrationProviderKey(provider);
  const renderIcon = providerKey ? PROVIDER_ICON_RENDERERS[providerKey] : null;

  if (renderIcon) {
    return renderIcon(size);
  }

  return <CheckCircle className="text-[#2FB344]" style={{ width: size, height: size }} />;
}

IntegrationProviderIcon.propTypes = {
  provider: PropTypes.string,
  size: PropTypes.number,
};
